import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { useAuth, can, ROLE, type Permission } from './lib/auth';
import { Layout } from './components/Layout';
import { LoginPage } from './pages/Login';
import { DashboardPage, GreetingDashboardPage } from './pages/Dashboard';
import { OrdersPage } from './pages/Orders';
import { ClientsPage } from './pages/Clients';
import { ContractorsPage } from './pages/Contractors';
import { QualityDashboardPage } from './pages/QualityDashboard';
import { MasterFilesPage } from './pages/MasterFiles';
import { ClientFormPage } from './pages/ClientForm';
import { ClientPrintPage, VendorPrintPage } from './pages/RegistrationPrint';
import { GrnPrintPage } from './pages/Grn';
import { BillPrintPage } from './pages/ContractorBills';
import { SimpleMasterList, SimpleMasterForm, type SimpleMasterPageConfig } from './components/SimpleMasterPage';
import {
  VENDORS, MACHINES, SHEETS, PAPER_TYPES, TOP_FINISHES,
  HOLIDAYS, NEWS, EMPLOYEES, DEPARTMENTS, DESIGNATIONS, PANTONE_COLORS,
} from './pages/masters';
import { DieDesignsPage } from './pages/DieDesigns';
import { DieDesignFormPage } from './pages/DieDesignForm';
import { ProductsPage } from './pages/Products';
import { ProductFormPage } from './pages/ProductForm';
import { ProductCardPrintPage } from './pages/ProductCardPrint';
import { PurchaseOrdersPage } from './pages/PurchaseOrders';
import { PurchaseOrderFormPage } from './pages/PurchaseOrderForm';
import { JobOrdersPage } from './pages/JobOrders';
import { JobOrderFormPage } from './pages/JobOrderForm';
import { JobCardPrintPage, JobCardHistoryPrintPage } from './pages/JobCardPrint';
import { PurchaseOrderPrintPage } from './pages/PurchaseOrderPrint';
import Slips from './pages/Slips';
import { DispatchesPage } from './pages/Dispatches';
import { DispatchFormPage } from './pages/DispatchForm';
import { ProductionEntryPage } from './pages/ProductionEntry';
import { ReportsPage } from './pages/Reports';
import { DodReportPage } from './pages/DodReport';
import { FlowQueuePage } from './pages/FlowQueue';
import { ArtworksPage } from './pages/Artworks';
import { ApprovalRequestsPage } from './pages/ApprovalRequests';
import { UsersPage } from './pages/Users';
import { SlaSettingsPage } from './pages/SlaSettings';
import { WipReportPage } from './pages/WipReport';
import { PlannerPage } from './pages/Planner';
import { InventoryPage } from './pages/Inventory';
import { PaperStockDashboardPage } from './pages/PaperStockDashboard';
import { EditRequestsPage } from './pages/EditRequests';
import { PrintLogPage } from './pages/PrintLog';
import { ComingSoonPage } from './pages/ComingSoon';
import { CuttingQueuePage } from './pages/CuttingQueue';
import { PrintingQueuePage } from './pages/PrintingQueue';
import { BarcodesPage } from './pages/Barcodes';

/** Where a CRM is allowed to go. Scope, not capability — see Layout.tsx. */
const CRM_ALLOWED_PATHS = [
  '/', '/dashboard', '/queue/crm', '/login',
  '/wip-report',
  // Look up a job order: the list, its read-only view and the job card.
  // Creating / editing stays behind CREATE_JOB / EDIT_JOB, which CRM lacks.
  '/job-orders',
];

const PLANNER_ALLOWED_PATHS = [
  '/', '/dashboard', '/planner', '/login',
  '/wip-report', '/queue/cutting',
  // Boards stock and its Stock Movement report, to look and search
  // (VIEW_BOARDS) - not its other tabs.
  '/inventory/boards', '/inventory/boards/reports',
];

// Purchase buys boards and stores.  Customer POs, packaging slips and
// production entry are other desks' work: closed to them here, in the
// sidebar and on the API.
const PURCHASE_BLOCKED_PATHS = ['/purchase-orders', '/slips', '/production'];

// Admin has no Performance, Job Orders or Reports.  A job card's print page
// stays open (other pages link to it).
const ADMIN_BLOCKED_PATHS = ['/dod-report', '/job-orders', '/reports'];

// Accounts keeps the clients and vendors and looks at the inventories.
const ACCOUNTS_ALLOWED_PATHS = [
  '/', '/dashboard', '/clients', '/vendors', '/inventory', '/login',
];
const ACCOUNTS_ALLOWED_PREFIXES = ['/clients/', '/vendors/', '/inventory/', '/grn/'];

// Quality approves or rejects production entries, and looks job cards up:
// the Job Orders list, a card's read-only view and its print page (they hold
// no CREATE_JOB / EDIT_JOB).
const QUALITY_ALLOWED_PATHS = [
  '/', '/dashboard', '/quality', '/quality/master-files', '/login', '/job-orders',
];
const QUALITY_ALLOWED_PREFIX = '/job-orders/';

// Store issues raw material: the greeting dashboard and the Raw Material page.
const STORE_ALLOWED_PATHS = ['/', '/dashboard', '/login'];
const STORE_ALLOWED_PREFIX = '/inventory/raw-material';

// Printing records what was printed against the plan, and nothing else.
const PRINTING_ALLOWED_PATHS = ['/', '/dashboard', '/queue/printing', '/login'];

const CUTTING_ALLOWED_PATHS = [
  '/', '/dashboard', '/queue/cutting', '/login',
];

// A Designer lands on the Design Queue by default and mostly lives
// there.  They can still open a PO / product / job order they need
// to look at, hit their own performance report, and log out.  The Job
// Orders list is theirs to look a job card up and print it — view only,
// they hold no CREATE_JOB / EDIT_JOB.  Everything else redirects to /dashboard.
const DESIGNER_ALLOWED_PATHS = [
  '/', '/dashboard', '/queue/design', '/artworks', '/barcodes', '/job-orders', '/reports/performance', '/login',
];

function Protected({ children, noLayout, perm, superAdminOnly }: {
  children: React.ReactNode;
  noLayout?: boolean;
  /** Capability the page needs (any one of a list). Without it, bounce to the dashboard. */
  perm?: Permission | Permission[];
  /** Restrict the page to Super Admin. */
  superAdminOnly?: boolean;
}) {
  const { user } = useAuth();
  // useLocation, not window.location: reading the global directly meant the
  // check ran against whatever the URL was when this component last rendered,
  // so a client-side navigation could slip past it entirely.
  const location = useLocation();

  if (!user) return <Navigate to="/login" replace />;

  const isCrmAllowed = CRM_ALLOWED_PATHS.includes(location.pathname)
    || location.pathname.startsWith('/purchase-orders/')
    || location.pathname.startsWith('/products/')
    || location.pathname.startsWith('/job-orders/');

  if (user.rollId === ROLE.CRM && !isCrmAllowed) {
    return <Navigate to="/dashboard" replace />;
  }

  if (user.rollId === ROLE.Planner
      && !PLANNER_ALLOWED_PATHS.includes(location.pathname)) {
    return <Navigate to="/planner" replace />;
  }

  if (user.rollId === ROLE.Accounts
      && !ACCOUNTS_ALLOWED_PATHS.includes(location.pathname)
      && !ACCOUNTS_ALLOWED_PREFIXES.some((p) => location.pathname.startsWith(p))) {
    return <Navigate to="/clients" replace />;
  }

  if (user.rollId === ROLE.Purchase
      && PURCHASE_BLOCKED_PATHS.some((p) => location.pathname === p || location.pathname.startsWith(`${p}/`))) {
    return <Navigate to="/dashboard" replace />;
  }

  if (user.rollId === ROLE.Admin
      && !location.pathname.endsWith('/print')
      && ADMIN_BLOCKED_PATHS.some((p) => location.pathname === p || location.pathname.startsWith(`${p}/`))) {
    return <Navigate to="/dashboard" replace />;
  }

  if (user.rollId === ROLE.Quality && !QUALITY_ALLOWED_PATHS.includes(location.pathname)
      && !location.pathname.startsWith(QUALITY_ALLOWED_PREFIX)) {
    return <Navigate to="/quality" replace />;
  }

  if (user.rollId === ROLE.Store
      && !STORE_ALLOWED_PATHS.includes(location.pathname)
      && location.pathname !== STORE_ALLOWED_PREFIX
      && !location.pathname.startsWith(`${STORE_ALLOWED_PREFIX}/`)) {
    return <Navigate to={STORE_ALLOWED_PREFIX} replace />;
  }

  if (user.rollId === ROLE.Printing && !PRINTING_ALLOWED_PATHS.includes(location.pathname)) {
    return <Navigate to="/queue/printing" replace />;
  }

  if (user.rollId === ROLE.Cutting && !CUTTING_ALLOWED_PATHS.includes(location.pathname)) {
    return <Navigate to="/queue/cutting" replace />;
  }

  if (user.rollId === ROLE.Designers
      && !DESIGNER_ALLOWED_PATHS.includes(location.pathname)
      && !location.pathname.startsWith('/purchase-orders/')
      && !location.pathname.startsWith('/products/')
      && !location.pathname.startsWith('/job-orders/')) {
    return <Navigate to="/dashboard" replace />;
  }

  if (perm && !(Array.isArray(perm) ? perm.some((p) => can(user, p)) : can(user, perm))) {
    return <Navigate to="/dashboard" replace />;
  }

  if (superAdminOnly && user.rollId !== ROLE.SuperAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  return noLayout ? <>{children}</> : <Layout>{children}</Layout>;
}

/**
 * Quality's dashboard is the greeting, birthdays and holidays only — their
 * approvals live on the Quality page. Everyone else gets the order dashboard.
 */
function RoleHome() {
  const { user } = useAuth();
  return user?.rollId === ROLE.Quality || user?.rollId === ROLE.Store || user?.rollId === ROLE.Printing
    ? <GreetingDashboardPage /> : <DashboardPage />;
}

function masterRoutes(cfg: SimpleMasterPageConfig, superAdminOnly = false) {
  const list = <Protected superAdminOnly={superAdminOnly}><SimpleMasterList cfg={cfg} /></Protected>;
  const form = <Protected superAdminOnly={superAdminOnly}><SimpleMasterForm cfg={cfg} /></Protected>;
  return (
    <>
      <Route path={cfg.routeBase} element={list} />
      <Route path={`${cfg.routeBase}/new`} element={form} />
      <Route path={`${cfg.routeBase}/:id/edit`} element={form} />
    </>
  );
}

export default function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />

      <Route path="/" element={<Protected><RoleHome /></Protected>} />
      <Route path="/dashboard" element={<Protected><RoleHome /></Protected>} />

      <Route path="/clients" element={<Protected><ClientsPage /></Protected>} />
      {/* Contacts, default rates, who stopped working: Super Admin only. */}
      <Route path="/contractors" element={<Protected perm="EDIT_CONTRACTORS"><ContractorsPage /></Protected>} />
      <Route path="/quality" element={<Protected perm="VIEW_QC_QUEUE"><QualityDashboardPage /></Protected>} />
      <Route path="/quality/master-files" element={<Protected perm="VIEW_MASTER_FILES"><MasterFilesPage /></Protected>} />
      <Route path="/clients/new" element={<Protected><ClientFormPage /></Protected>} />
      <Route path="/clients/:id/edit" element={<Protected><ClientFormPage /></Protected>} />
      <Route path="/clients/:id/print" element={<Protected noLayout><ClientPrintPage /></Protected>} />

      <Route path="/vendors/:id/print" element={<Protected noLayout><VendorPrintPage /></Protected>} />
      {masterRoutes(VENDORS)}
      {masterRoutes(MACHINES)}
      {masterRoutes(SHEETS)}
      {masterRoutes(PAPER_TYPES)}
      {masterRoutes(TOP_FINISHES)}
      {masterRoutes(HOLIDAYS)}
      {masterRoutes(NEWS)}
      {/* Employees and designations: Super Admin only. */}
      {masterRoutes(EMPLOYEES, true)}
      {masterRoutes(DEPARTMENTS)}
      {masterRoutes(DESIGNATIONS, true)}
      {masterRoutes(PANTONE_COLORS)}

      <Route path="/die-designs" element={<Protected><DieDesignsPage /></Protected>} />
      <Route path="/die-designs/new" element={<Protected><DieDesignFormPage /></Protected>} />
      <Route path="/die-designs/:id/edit" element={<Protected><DieDesignFormPage /></Protected>} />

      <Route path="/products" element={<Protected><ProductsPage /></Protected>} />
      <Route path="/products/new" element={<Protected><ProductFormPage /></Protected>} />
      <Route path="/products/:id/edit" element={<Protected><ProductFormPage /></Protected>} />
      <Route path="/products/:id/print" element={<Protected noLayout><ProductCardPrintPage /></Protected>} />

      {/* Lists and detail pages stay open — these records are visible to the
          whole office. Only the CREATE routes are gated, because a viewer
          landing on a blank "New" form has nothing to look at and no way to
          save it. Detail forms render READ-ONLY without the EDIT permission,
          so a viewer sees the real record with no Save button. */}
      <Route path="/purchase-orders" element={<Protected><PurchaseOrdersPage /></Protected>} />
      <Route path="/purchase-orders/new" element={<Protected perm="CREATE_PO"><PurchaseOrderFormPage /></Protected>} />
      <Route path="/purchase-orders/:id/edit" element={<Protected><PurchaseOrderFormPage /></Protected>} />
      <Route path="/purchase-orders/:id/print" element={<Protected noLayout><PurchaseOrderPrintPage /></Protected>} />
      <Route path="/grn/:id/print" element={<Protected noLayout perm="VIEW_INVENTORY"><GrnPrintPage /></Protected>} />
      <Route path="/billing/:id/print" element={<Protected noLayout perm="VIEW_BILLING"><BillPrintPage /></Protected>} />

      <Route path="/job-orders" element={<Protected><JobOrdersPage /></Protected>} />
      <Route path="/job-orders/new" element={<Protected perm="CREATE_JOB"><JobOrderFormPage /></Protected>} />
      <Route path="/job-orders/:id/edit" element={<Protected><JobOrderFormPage /></Protected>} />
      <Route path="/job-orders/:id/print" element={<Protected noLayout><JobCardPrintPage /></Protected>} />
      <Route path="/job-orders/history/:historyId/print" element={<Protected noLayout><JobCardHistoryPrintPage /></Protected>} />

      <Route path="/slips" element={<Protected><Slips /></Protected>} />

      <Route path="/dispatches" element={<Protected><DispatchesPage /></Protected>} />
      <Route path="/dispatches/new" element={<Protected><DispatchFormPage /></Protected>} />
      <Route path="/dispatches/:id/edit" element={<Protected><DispatchFormPage /></Protected>} />
      <Route path="/production" element={<Protected><ProductionEntryPage /></Protected>} />

      <Route path="/reports" element={<Protected perm="VIEW_OVERSIGHT"><ReportsPage /></Protected>} />
      {/* Read-only performance view. Gated on VIEW_OVERSIGHT. */}
      <Route path="/dod-report" element={<Protected perm="VIEW_OVERSIGHT"><DodReportPage /></Protected>} />
      {/* Every PO in the flow and where it is - was a dashboard panel. */}
      <Route path="/orders" element={<Protected perm="VIEW_OVERSIGHT"><OrdersPage /></Protected>} />

      {/* Routes gate on the VIEW permission; the action controls inside each
          queue gate separately on the action permission. */}
      <Route path="/artworks" element={<Protected perm="MARK_DESIGN_DONE"><ArtworksPage /></Protected>} />
      <Route path="/queue/crm" element={<Protected perm="VIEW_CRM_QUEUE"><FlowQueuePage kind="crm" /></Protected>} />
      <Route path="/queue/design" element={<Protected perm="MARK_DESIGN_DONE"><FlowQueuePage kind="design" /></Protected>} />
      <Route path="/queue/purchase" element={<Protected perm="VIEW_PURCHASE_QUEUE"><FlowQueuePage kind="purchase" /></Protected>} />
      <Route path="/queue/cutting" element={<Protected perm="VIEW_CUTTING_QUEUE"><CuttingQueuePage /></Protected>} />
      <Route path="/queue/printing" element={<Protected perm="VIEW_PRINTING_QUEUE"><PrintingQueuePage /></Protected>} />
      <Route path="/barcodes" element={<Protected perm="MANAGE_BARCODES"><BarcodesPage /></Protected>} />
      <Route path="/queue/dispatch" element={<Protected perm="VIEW_DISPATCH_QUEUE"><FlowQueuePage kind="dispatch" /></Protected>} />
      <Route path="/approval-requests" element={<Protected perm="VIEW_OVERSIGHT"><ApprovalRequestsPage /></Protected>} />
      <Route path="/users" element={<Protected perm="MANAGE_USERS"><UsersPage /></Protected>} />
      <Route path="/settings/tat" element={<Navigate to="/settings/sla" replace />} />
      <Route path="/settings/sla" element={<Protected superAdminOnly><SlaSettingsPage /></Protected>} />
      <Route path="/planner" element={<Protected perm="VIEW_PLANNER"><PlannerPage /></Protected>} />
      {/* Inventory routes — one page, three scopes. The scope in the URL
          drives the category filter and the New-Item defaults, so /boards,
          /raw-material and /spare-parts each feel like their own inventory
          while sharing all the plumbing. Tab is optional; the page falls
          back to the stock dashboard for the scope. */}
      <Route path="/inventory" element={<Navigate to="/inventory/raw-material" replace />} />
      {/* Boards get their own domain-specific dashboard (SKU + paper spec).
          Raw Material and Spare Parts still share the generic Inventory page. */}
      <Route path="/inventory/boards" element={<Protected perm={['VIEW_INVENTORY', 'VIEW_BOARDS']}><PaperStockDashboardPage /></Protected>} />
      <Route path="/inventory/boards/:tab" element={<Protected perm={['VIEW_INVENTORY', 'VIEW_BOARDS']}><PaperStockDashboardPage /></Protected>} />
      <Route path="/inventory/:scope" element={<Protected perm="VIEW_INVENTORY"><InventoryPage /></Protected>} />
      <Route path="/inventory/:scope/:tab" element={<Protected perm="VIEW_INVENTORY"><InventoryPage /></Protected>} />
      <Route path="/edit-requests" element={<Protected perm="APPROVE_JOB_EDIT"><EditRequestsPage /></Protected>} />
      <Route path="/print-log" element={<Protected perm="MANAGE_USERS"><PrintLogPage /></Protected>} />
      <Route path="/wip-report" element={<Protected><WipReportPage /></Protected>} />
      <Route path="/estimations" element={<Protected><ComingSoonPage title="Estimations" /></Protected>} />
      <Route path="/progress" element={<Protected><ComingSoonPage title="Progress Report" /></Protected>} />

      <Route path="*" element={<Protected><ComingSoonPage title="Not found" /></Protected>} />
    </Routes>
  );
}
